import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, ChevronDown, Stethoscope, UserRound, Building2, Briefcase, Sparkles, FileText, Plane, Languages, MapPin, Heart, Activity, Hotel, Video } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import LanguageSelector from '../../components/LanguageSelector';
import { useTranslation } from '../../hooks/useTranslation';
import Logo from '../../components/Logo';

export default function ServicePageLayout({ 
  title, 
  subtitle, 
  icon: Icon, 
  heroColor = 'from-orange-500 to-red-600',
  children,
  enquiryTitle = 'Service Enquiry'
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    message: ''
  });

  const handleEnquiry = (e) => {
    e.preventDefault();
    toast.success(t('services.enquiryForm.successMessage'));
    setShowEnquiryForm(false);
    setFormData({ name: '', email: '', phone: '', country: '', message: '' });
  };

  const services = [
    { name: t('servicesMenu.airportPickup'), path: '/services/airport-pickup', icon: Plane },
    { name: t('servicesMenu.languageInterpreter'), path: '/services/language-interpreter', icon: Languages },
    { name: t('servicesMenu.surroundingTours'), path: '/services/surrounding-tours', icon: MapPin },
    { name: t('servicesMenu.nursingCare'), path: '/services/nursing-care', icon: Heart },
    { name: t('servicesMenu.physiotherapist'), path: '/services/physiotherapist', icon: Activity },
    { name: t('servicesMenu.travelAccommodation'), path: '/services/travel-accommodation', icon: Hotel },
    { name: t('servicesMenu.videoConsultations'), path: '/services/video-consultations', icon: Video },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-orange-100 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Logo size="md" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                MedRahi
              </h1>
              <p className="text-xs text-gray-600">{t('landing.healthcareWithoutBorders')}</p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/treatments')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <Stethoscope className="w-4 h-4" />
              {t('nav.treatments')}
            </button>
            <button onClick={() => navigate('/doctors')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <UserRound className="w-4 h-4" />
              {t('nav.doctors')}
            </button>
            <button onClick={() => navigate('/hospitals')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <Building2 className="w-4 h-4" />
              {t('nav.hospitals')}
            </button>
            
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors flex items-center gap-1.5" data-testid="services-dropdown-trigger">
                  <Briefcase className="w-4 h-4" />
                  {t('nav.services')}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                {services.map((service) => {
                  const ServiceIcon = service.icon;
                  return (
                    <DropdownMenuItem 
                      key={service.path} 
                      onClick={() => navigate(service.path)}
                      className="cursor-pointer hover:bg-orange-50 flex items-center gap-2"
                      data-testid={`service-link-${service.path.split('/').pop()}`}
                    >
                      <ServiceIcon className="w-4 h-4 text-orange-500" />
                      {service.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button onClick={() => navigate('/wellness')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" />
              {t('nav.wellness')}
            </button>
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors"
              data-testid="header-login-button"
            >
              {t('nav.signIn')}
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              data-testid="header-register-button"
            >
              {t('nav.getStarted')}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${heroColor} text-white pt-32 pb-20`}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <button 
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
              data-testid="back-to-home-button"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('nav.backToHome')}
            </button>
            {Icon && <Icon className="w-16 h-16 mx-auto mb-6" />}
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="service-page-title">{title}</h1>
            <p className="text-xl text-white/90">{subtitle}</p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-5xl mx-auto">
          {children}
          
          {/* CTA Button */}
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className={`bg-gradient-to-r ${heroColor} hover:opacity-90 text-white px-12 py-6 text-xl`}
              onClick={() => setShowEnquiryForm(true)}
              data-testid="enquire-now-button"
            >
              {t('services.enquireNow')}
            </Button>
            <p className="text-gray-600 mt-4">{t('services.responseTime')}</p>
          </div>
        </div>
      </div>

      {/* Enquiry Form Dialog */}
      <Dialog open={showEnquiryForm} onOpenChange={setShowEnquiryForm}>
        <DialogContent className="max-w-md" data-testid="enquiry-modal">
          <DialogHeader>
            <DialogTitle>{enquiryTitle}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEnquiry} className="space-y-4 mt-4" data-testid="enquiry-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.enquiryForm.fullName')} *</label>
              <Input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="enquiry-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.enquiryForm.email')} *</label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="enquiry-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.enquiryForm.phone')} *</label>
              <Input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="enquiry-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.enquiryForm.country')} *</label>
              <Input
                type="text"
                required
                placeholder={t('services.enquiryForm.countryPlaceholder')}
                value={formData.country}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                data-testid="enquiry-country-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.enquiryForm.additionalRequirements')}</label>
              <Textarea
                rows={3}
                placeholder={t('services.enquiryForm.additionalPlaceholder')}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                data-testid="enquiry-message-input"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEnquiryForm(false)} data-testid="enquiry-cancel-button">
                {t('services.enquiryForm.cancel')}
              </Button>
              <Button type="submit" className={`bg-gradient-to-r ${heroColor}`} data-testid="enquiry-submit-button">
                {t('services.enquiryForm.submit')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Logo size="md" />
            <h3 className="font-heading text-xl font-bold text-white">MedRahi</h3>
          </div>
          <p className="text-sm text-gray-400">
            © 2024 MedRahi. All rights reserved. Made with ❤️ in India
          </p>
        </div>
      </footer>
    </div>
  );
}
